import React, { useMemo } from 'react'
import { useController } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { MultiSelectCombobox } from '../../../../legacy-design-system/combobox'
import { Box } from '../../../../legacy-design-system/form'
import { ComboboxItem } from '../../../../legacy-design-system/combobox/shared'
import { JiraFormFieldTypes } from './jira-form-field-types'

interface Item {
  name: string
  value: string
  group: string
}

const TYPES_GROUP = 'TYPES'
const SEVERITY_GROUP = 'SEVERITY'
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Note']

const useServerlessResultItems = () => {
  const { formatMessage } = useIntl()

  return useMemo(() => {
    const typesCategoryTitle = formatMessage({
      id: 'title_integrationsSettings_serverlessResults_typesCategory'
    })
    const serverlessResultTypesItems: Item[] = [
      {
        name: 'Permissions',
        value: 'PERMISSIONS',
        group: TYPES_GROUP
      },
      {
        name: 'Exploits',
        value: 'EXPLOITS',
        group: TYPES_GROUP
      },
      {
        name: 'Dependencies',
        value: 'DEPENDENCIES',
        group: TYPES_GROUP
      }
    ]

    const severityCategoryTitle = formatMessage({
      id: 'title_severityLevel'
    })
    const serverlessResultSeverityItems: Item[] = SEVERITIES.map(
      (severity) => ({
        name: formatMessage({
          id: `title_vulnSeverityType${severity}`
        }),
        value: severity.toUpperCase(),
        group: SEVERITY_GROUP
      })
    )
    const items: Array<ComboboxItem<Item>> = [
      {
        name: severityCategoryTitle,
        items: serverlessResultSeverityItems
      },
      {
        name: typesCategoryTitle,
        items: serverlessResultTypesItems
      }
    ]

    return items
  }, [formatMessage])
}

const mapValuesToComboboxItem = (
  values: string[],
  items: Array<ComboboxItem<Item>>
) => {
  const allItems: Item[] = Array.prototype.concat(...items.map((v) => v.items))

  return values.map((value) => allItems.find((item) => item.value === value))
}

export const ServerlessResultsField = () => {
  const { formatMessage } = useIntl()

  const {
    field: { onChange: typeOnChange, value: typeValue }
  } = useController({ name: JiraFormFieldTypes.SERVERLESS_CATEGORIES })

  const {
    field: { onChange: severityOnChange, value: severityValue }
  } = useController({
    name: JiraFormFieldTypes.AUTO_CREATE_TICKETS_SEVERITIES
  })

  const handleOnChange = (newValues: Item[]) => {
    const newTypes = newValues.filter(({ group }) => group === TYPES_GROUP)
    typeOnChange(newTypes.map((value) => value.value))

    const newSeverities = newValues.filter(
      ({ group }) => group === SEVERITY_GROUP
    )
    severityOnChange(newSeverities.map((value) => value.value))
  }

  const items = useServerlessResultItems()

  return (
    <>
      <Box basis={10}>
        {formatMessage({
          id: 'title_integrationsSettings_serverlessResults_detail'
        })}
      </Box>
      <Box basis={10}>
        <MultiSelectCombobox
          values={
            mapValuesToComboboxItem([...typeValue, ...severityValue], items) ||
            []
          }
          label={formatMessage({
            id: 'title_integrationsSettings_serverlessResults'
          })}
          placeHolderTag={formatMessage({
            id: 'title_integrationsSettings_serverlessResults_allResults'
          })}
          items={items}
          itemToString={(item) => item?.name || '?'}
          onChange={handleOnChange}
          data-testid="serverless-accounts-multi-select"
        />
      </Box>
    </>
  )
}
